import { Component, computed, inject } from '@angular/core';
import { UwIcon } from '../../shared/ui/icon/uw-icon';
import { UwBadge } from '../../shared/ui/badge/uw-badge';
import { UwButton } from '../../shared/ui/button/uw-button';
import { UwProgressRing } from '../../shared/ui/progress-ring/uw-progress-ring';
import { UwProgress } from '../../shared/ui/progress/uw-progress';
import { UwAvatar } from '../../shared/ui/avatar/uw-avatar';
import { UwEmptyState } from '../../shared/ui/empty-state/uw-empty-state';
import { AccountStore } from '../../core/stores/account.store';
import { ToastService } from '../../core/services/toast.service';
import { DialogService } from '../../core/services/dialog.service';
import { CopilotStore } from '../../core/stores/copilot.store';
import { RISK_ASSESSMENTS, PRICING_RESULTS, REFERRALS, DOCUMENT_SUMMARIES } from '../../core/services/mock-data';
import { UwMoneyPipe, UwCompactMoneyPipe } from '../../shared/pipes/format.pipes';
import type { Account } from '../../core/models/account.model';

@Component({
  selector: 'uw-classic-profile-page',
  standalone: true,
  imports: [UwIcon, UwBadge, UwCompactMoneyPipe, UwEmptyState],
  template: `
    @if (account(); as acct) {
      <div class="grid grid-cols-1 gap-4 xl:grid-cols-[1.4fr_1fr]">
        <section class="rounded-xl border border-line bg-surface p-4 shadow-sm">
          <div class="flex items-start justify-between gap-3">
            <div>
              <h2 class="typo-heading text-primary">{{ acct.name }}</h2>
              <p class="mt-1 flex items-center gap-1.5 text-[12px] text-text-muted">
                <uw-icon name="map-pin" [size]="12" />
                {{ acct.address.street }}, {{ acct.address.city }}, {{ acct.address.state }} {{ acct.address.zip }}
              </p>
              <p class="mt-0.5 text-[11.5px] text-text-muted">NAICS {{ acct.naics }} — {{ acct.naicsDescription }}</p>
            </div>
            <uw-badge [tone]="acct.riskScore >= 70 ? 'success' : acct.riskScore >= 55 ? 'warning' : 'danger'" icon="gauge">
              {{ acct.riskLabel }} risk · {{ acct.riskScore }}
            </uw-badge>
          </div>

          <dl class="mt-4 grid grid-cols-2 gap-px overflow-hidden rounded-lg border border-line bg-line sm:grid-cols-4">
            <div class="bg-surface px-3 py-2.5"><dt class="typo-label">In business</dt><dd class="mt-0.5 text-[13px] font-semibold text-primary">{{ acct.yearsInBusiness }} yrs</dd></div>
            <div class="bg-surface px-3 py-2.5"><dt class="typo-label">Revenue</dt><dd class="mt-0.5 typo-mono text-[13px] font-semibold text-primary">{{ acct.annualRevenue | uwCompactMoney }}</dd></div>
            <div class="bg-surface px-3 py-2.5"><dt class="typo-label">Employees</dt><dd class="mt-0.5 typo-mono text-[13px] font-semibold text-primary">{{ acct.employees }}</dd></div>
            <div class="bg-surface px-3 py-2.5"><dt class="typo-label">Prior carrier</dt><dd class="mt-0.5 text-[13px] font-semibold text-primary">{{ acct.priorCarrier }}</dd></div>
          </dl>
        </section>

        <section class="rounded-xl border border-line bg-surface shadow-sm">
          <header class="border-b border-line px-4 py-3">
            <h3 class="typo-title text-primary">Corporate structure</h3>
          </header>
          <ul class="divide-y divide-line">
            @for (entity of acct.corporateStructure; track entity.name) {
              <li class="flex items-start gap-3 px-4 py-3">
                <span class="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-primary-soft text-primary">
                  <uw-icon name="building-2" [size]="13" />
                </span>
                <div class="min-w-0 flex-1">
                  <p class="flex items-center gap-2 text-[12.5px] font-semibold text-primary">
                    {{ entity.name }}
                    @if (entity.current) {
                      <uw-badge tone="ai" subtle>This submission</uw-badge>
                    }
                  </p>
                  <p class="mt-0.5 text-[11px] text-text-muted">{{ entity.kind }} · {{ entity.detail }}</p>
                </div>
              </li>
            }
          </ul>
        </section>
      </div>
    } @else {
      <uw-empty-state title="No account selected" icon="building" />
    }
  `,
})
export class ClassicProfilePage {
  protected readonly account = computed<Account | undefined>(() => this.accountStore.selectedAccount());
  private readonly accountStore = inject(AccountStore);
}

@Component({
  selector: 'uw-classic-risk-page',
  standalone: true,
  imports: [UwProgressRing, UwProgress, UwEmptyState],
  template: `
    @if (assessment(); as a) {
      <div class="grid grid-cols-1 gap-4 xl:grid-cols-[1fr_1.6fr]">
        <section class="flex items-center justify-center rounded-xl border border-line bg-surface p-6 shadow-sm">
          <uw-progress-ring [value]="a.compositeScore" [size]="140" [stroke]="10" [tone]="a.scoreTone" [label]="a.riskLabel" />
        </section>
        <section class="space-y-4">
          <div class="rounded-xl border border-line bg-surface p-4 shadow-sm">
            <p class="typo-label mb-2">Engine narrative</p>
            <p class="text-[12.5px] leading-relaxed text-secondary">{{ a.narrative }}</p>
            <div class="mt-3 space-y-3">
              @for (f of a.factors; track f.label) {
                <div>
                  <div class="mb-1 flex justify-between text-[11px]">
                    <span class="text-secondary">{{ f.label }}</span>
                    <span class="typo-mono font-semibold text-primary">{{ f.score }}</span>
                  </div>
                  <uw-progress [value]="f.score" [tone]="f.tone" [label]="f.label" />
                </div>
              }
            </div>
          </div>
          <div class="rounded-xl border border-success/25 bg-success-soft/40 p-4">
            <p class="typo-label mb-1 text-success">Recommendation</p>
            <p class="text-[12px] text-secondary">{{ a.recommendation }}</p>
          </div>
        </section>
      </div>
    } @else {
      <uw-empty-state title="No risk assessment" icon="gauge" />
    }
  `,
})
export class ClassicRiskPage {
  private readonly accountStore = inject(AccountStore);

  protected readonly assessment = computed(() => RISK_ASSESSMENTS[this.accountStore.selectedAccountId()] ?? null);
}

@Component({
  selector: 'uw-classic-pricing-page',
  standalone: true,
  imports: [UwButton, UwEmptyState, UwMoneyPipe],
  template: `
    @if (pricing(); as p) {
      <div class="space-y-4">
        <div class="flex flex-wrap items-center justify-between gap-2">
          <div>
            <h2 class="typo-title text-primary">Pricing & Rating</h2>
            <p class="text-[11.5px] text-text-muted">Rate version {{ p.rateVersion }} · {{ p.accountId }}</p>
          </div>
          <uw-button variant="ai" size="sm" icon="calculator" (click)="generate()">Generate Quote</uw-button>
        </div>

        <div class="grid grid-cols-1 gap-4 lg:grid-cols-[1.6fr_1fr]">
          <section class="overflow-hidden rounded-xl border border-line bg-surface shadow-sm">
            <table class="w-full border-collapse text-[12px]">
              <thead>
                <tr class="bg-surface-sunken/60">
                  <th class="typo-label px-3 py-2.5 text-left">Coverage</th>
                  <th class="typo-label px-3 py-2.5 text-left">Exposure</th>
                  <th class="typo-label px-3 py-2.5 text-right">Indicated</th>
                  <th class="typo-label px-3 py-2.5 text-right">Proposed</th>
                </tr>
              </thead>
              <tbody>
                @for (line of p.lines; track line.coverage) {
                  <tr class="border-t border-line">
                    <td class="px-3 py-2.5">
                      <p class="font-medium text-primary">{{ line.coverage }}</p>
                      <p class="text-[10.5px] text-text-muted">{{ line.baseRate }} · Mod {{ line.experienceMod }} · {{ line.scheduleCredit }}</p>
                    </td>
                    <td class="px-3 py-2.5 text-secondary">{{ line.exposure }}</td>
                    <td class="px-3 py-2.5 text-right typo-mono text-secondary">{{ line.indicated | uwMoney }}</td>
                    <td class="px-3 py-2.5 text-right typo-mono font-semibold text-primary">{{ line.proposed | uwMoney }}</td>
                  </tr>
                }
              </tbody>
            </table>
            <div class="flex items-center justify-between border-t border-line bg-surface-sunken/40 px-3 py-2.5">
              <span class="text-[12px] font-semibold text-primary">Total</span>
              <div class="flex items-center gap-4">
                <span class="typo-mono text-[12px] text-secondary">{{ p.indicatedTotal | uwMoney }}</span>
                <span class="typo-mono text-[13px] font-bold text-primary">{{ p.proposedTotal | uwMoney }}</span>
              </div>
            </div>
          </section>

          <div class="space-y-4">
            <section class="rounded-xl border border-line bg-surface p-4 shadow-sm">
              <p class="typo-label mb-2">Comparison</p>
              <ul class="space-y-2">
                @for (c of p.comparison; track c.label) {
                  <li class="flex items-center justify-between text-[12px]">
                    <span class="text-secondary">{{ c.label }}</span>
                    <span class="typo-mono font-semibold" [class]="c.tone === 'accent' ? 'text-ai' : c.tone === 'info' ? 'text-info' : 'text-primary'">
                      {{ c.value | uwMoney }}
                    </span>
                  </li>
                }
              </ul>
              <p class="mt-3 rounded-md bg-surface-sunken px-2.5 py-2 text-[11px] leading-snug text-secondary">{{ p.note }}</p>
            </section>

            <section class="rounded-xl border border-line bg-surface p-4 shadow-sm">
              <p class="typo-label mb-2">Umbrella layers</p>
              <ul class="space-y-2">
                @for (layer of p.umbrellaLayers; track layer.layer) {
                  <li class="flex items-center justify-between gap-2 border-b border-line pb-2 text-[11.5px] last:border-0 last:pb-0">
                    <span class="font-medium text-primary">{{ layer.layer }} · {{ layer.limit }}</span>
                    <span class="text-right text-text-muted">{{ layer.attachment }} · {{ layer.premium }}</span>
                  </li>
                }
              </ul>
            </section>
          </div>
        </div>
      </div>
    } @else {
      <uw-empty-state title="No pricing result" icon="calculator" />
    }
  `,
})
export class ClassicPricingPage {
  private readonly accountStore = inject(AccountStore);
  private readonly dialog = inject(DialogService);
  private readonly toast = inject(ToastService);

  protected readonly pricing = computed(() => PRICING_RESULTS[this.accountStore.selectedAccountId()] ?? null);

  protected generate(): void {
    this.dialog.confirm({
      title: 'Generate quote',
      message: 'Generate the quote from current pricing and send it to the broker?',
      confirmLabel: 'Generate & send',
      onConfirm: () => this.toast.success('Quote generated', 'The quote was sent to the broker for review.'),
    });
  }
}

@Component({
  selector: 'uw-classic-referrals-page',
  standalone: true,
  imports: [UwIcon, UwBadge, UwButton, UwAvatar, UwEmptyState],
  template: `
    @if (referral(); as r) {
      <div class="grid grid-cols-1 gap-4 xl:grid-cols-[1.4fr_1fr]">
        <section class="rounded-xl border border-line bg-surface p-4 shadow-sm">
          <div class="mb-3 flex items-center justify-between">
            <h3 class="typo-title text-primary">Approval chain</h3>
            <uw-badge tone="warning" icon="clock">{{ r.sla }}</uw-badge>
          </div>
          <ol class="relative space-y-4 before:absolute before:bottom-2 before:left-4 before:top-2 before:w-px before:bg-line">
            @for (step of r.steps; track step.id) {
              <li class="relative flex items-start gap-3 pl-0">
                <span class="z-10 flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-[11px] font-bold" [class]="stepClass(step)">
                  @if (step.state === 'done') {
                    <uw-icon name="check" [size]="13" />
                  } @else {
                    {{ $index + 1 }}
                  }
                </span>
                <div class="min-w-0 pt-0.5">
                  <p class="text-[12.5px] font-semibold text-primary">{{ step.title }}</p>
                  @if (step.meta) {
                    <p class="mt-0.5 text-[11px] leading-snug text-text-muted">{{ step.meta }}</p>
                  }
                </div>
              </li>
            }
          </ol>
        </section>

        <section class="space-y-4">
          <div class="rounded-xl border border-line bg-surface shadow-sm">
            <header class="border-b border-line px-4 py-3"><h3 class="typo-title text-primary">Discussion</h3></header>
            <ul class="divide-y divide-line">
              @for (msg of r.discussion; track $index) {
                <li class="flex items-start gap-3 px-4 py-3">
                  <uw-avatar [initials]="msg.initials" [label]="msg.author" size="sm" />
                  <div class="min-w-0 flex-1">
                    <p class="flex items-baseline gap-2 text-[11.5px]">
                      <span class="font-semibold text-primary">{{ msg.author }}</span>
                      <span class="text-[10px] text-text-muted">{{ msg.role }} · {{ msg.time }}</span>
                    </p>
                    <p class="mt-1 text-[12px] leading-relaxed text-secondary">{{ msg.text }}</p>
                  </div>
                </li>
              }
            </ul>
          </div>

          <div class="rounded-xl border border-line bg-surface p-4 shadow-sm">
            <p class="typo-label mb-2">Decision</p>
            <div class="flex flex-wrap gap-2">
              <uw-button variant="success" size="sm" icon="check" (click)="decide('approved')">Approve</uw-button>
              <uw-button variant="secondary" size="sm" icon="pencil" (click)="decide('approved with conditions')">Approve w/ conditions</uw-button>
              <uw-button variant="danger-outline" size="sm" icon="x" (click)="decide('declined')">Decline</uw-button>
            </div>
          </div>
        </section>
      </div>
    } @else {
      <uw-empty-state title="No active referral" icon="arrow-up" />
    }
  `,
})
export class ClassicReferralsPage {
  private readonly accountStore = inject(AccountStore);
  private readonly toast = inject(ToastService);

  protected readonly referral = computed(() => REFERRALS[this.accountStore.selectedAccountId()] ?? null);

  protected stepClass(step: { state: string }): string {
    return step.state === 'done'
      ? 'bg-success text-white'
      : step.state === 'current'
        ? 'bg-accent text-white ring-2 ring-accent/30'
        : 'bg-surface-sunken text-text-muted border border-line';
  }

  protected decide(action: string): void {
    this.toast.success(`Referral ${action}`, 'The decision was recorded and the chain updated.');
  }
}

@Component({
  selector: 'uw-classic-documents-page',
  standalone: true,
  imports: [UwIcon, UwBadge, UwEmptyState],
  template: `
    @if (docs(); as d) {
      <div class="grid grid-cols-1 gap-4 xl:grid-cols-[1fr_1.3fr]">
        <section class="rounded-xl border border-line bg-surface shadow-sm">
          <header class="border-b border-line px-4 py-3">
            <h3 class="typo-title text-primary">Document checklist</h3>
          </header>
          <ul class="divide-y divide-line">
            @for (item of d.checklist; track item.name) {
              <li class="flex items-center gap-3 px-4 py-3">
                <span class="flex h-6 w-6 shrink-0 items-center justify-center rounded-md" [class]="markClass(item.mark)">
                  <uw-icon [name]="markIcon(item.mark)" [size]="12" />
                </span>
                <div class="min-w-0 flex-1">
                  <p class="text-[12.5px] font-medium text-primary">{{ item.name }}</p>
                  <p class="text-[10.5px] text-text-muted">{{ item.meta }}</p>
                </div>
                <uw-badge [tone]="item.tone" subtle>{{ item.status }}</uw-badge>
              </li>
            }
          </ul>
        </section>

        <section class="overflow-hidden rounded-xl border border-line bg-surface shadow-sm">
          <header class="flex items-center gap-2 border-b border-line px-4 py-3">
            <uw-icon name="file" [size]="14" class="text-ai" />
            <div class="min-w-0 flex-1">
              <h3 class="truncate text-[13px] font-semibold text-primary">{{ d.preview.title }}</h3>
              <p class="text-[10.5px] text-text-muted">{{ d.preview.pageLabel }}</p>
            </div>
          </header>
          <div class="p-4">
            <pre class="whitespace-pre-wrap rounded-lg bg-surface-sunken p-3 font-mono text-[11px] leading-relaxed text-secondary">{{ d.preview.lines.join('\n') }}</pre>
            <div class="mt-3 rounded-lg border border-ai/25 bg-ai-soft/40 p-3">
              <p class="typo-label mb-1 flex items-center gap-1 text-ai"><uw-icon name="sparkles" [size]="11" /> AI Summary</p>
              <p class="text-[11.5px] leading-relaxed text-secondary">{{ d.preview.aiSummary }}</p>
              <p class="mt-2 flex items-start gap-1.5 text-[10.5px] leading-snug text-warning">
                <uw-icon name="alert-triangle" [size]="11" class="mt-0.5 shrink-0" />
                {{ d.preview.aiNote }}
              </p>
            </div>
          </div>
        </section>
      </div>
    } @else {
      <uw-empty-state title="No documents on file" icon="document" />
    }
  `,
})
export class ClassicDocumentsPage {
  private readonly accountStore = inject(AccountStore);

  protected readonly docs = computed(() => DOCUMENT_SUMMARIES[this.accountStore.selectedAccountId()] ?? null);

  protected markClass(mark: string): string {
    const classes: Record<string, string> = {
      check: 'bg-success-soft text-success',
      exclamation: 'bg-warning-soft text-warning',
      x: 'bg-danger-soft text-danger',
      ellipsis: 'bg-surface-sunken text-text-muted',
    };
    return classes[mark] ?? 'bg-surface-sunken text-text-muted';
  }

  protected markIcon(mark: string): string {
    const icons: Record<string, string> = {
      check: 'check',
      exclamation: 'alert-triangle',
      x: 'x',
      ellipsis: 'more-horizontal',
    };
    return icons[mark] ?? 'more-horizontal';
  }
}

@Component({
  selector: 'uw-classic-quote-page',
  standalone: true,
  imports: [UwIcon, UwButton, UwEmptyState, UwMoneyPipe],
  template: `
    @if (pricing(); as p) {
      <div class="space-y-4">
        <div class="flex items-center justify-between gap-2 rounded-xl border border-success/30 bg-success-soft/40 p-4">
          <div>
            <h2 class="typo-title text-primary">Quote & Policy Integration</h2>
            <p class="text-[11.5px] text-secondary">Proposed premium {{ p.proposedTotal | uwMoney }} · status: ready to bind</p>
          </div>
          <uw-button variant="success" size="sm" icon="shield-check" (click)="bind()">Bind policy</uw-button>
        </div>
        <section class="rounded-xl border border-line bg-surface p-4 shadow-sm">
          <p class="typo-label mb-2">Bind checklist</p>
          <ul class="space-y-2 text-[12px]">
            <li class="flex items-center gap-2"><uw-icon name="check" [size]="13" class="text-success" /> Appetite rules pass</li>
            <li class="flex items-center gap-2"><uw-icon name="check" [size]="13" class="text-success" /> OFAC screen cleared</li>
            <li class="flex items-center gap-2"><uw-icon name="check" [size]="13" class="text-success" /> Pricing within authority</li>
            <li class="flex items-center gap-2"><uw-icon name="alert-triangle" [size]="13" class="text-warning" /> TIV authority referral pending manager approval</li>
          </ul>
        </section>
      </div>
    } @else {
      <uw-empty-state title="No pricing result" icon="file-check" />
    }
  `,
})
export class ClassicQuotePage {
  private readonly accountStore = inject(AccountStore);
  private readonly toast = inject(ToastService);
  private readonly dialog = inject(DialogService);
  private readonly copilot = inject(CopilotStore);

  protected readonly pricing = computed(() => PRICING_RESULTS[this.accountStore.selectedAccountId()] ?? null);

  protected bind(): void {
    this.dialog.confirm({
      title: 'Bind policy',
      message: 'Bind this policy and issue the binder to the broker?',
      tone: 'success',
      confirmLabel: 'Bind & issue',
      onConfirm: () => {
        this.copilot.setActionBanner('Policy bound — binder issued to broker');
        this.toast.success('Policy bound', 'The binder was issued to the broker.');
      },
    });
  }
}
